import React from 'react';
import { Post, UpdatePostData } from '../../services/postService';
import AuthenticatedImage from '../media/AuthenticatedImage';
import { useClient } from '../../contexts/ClientContext';
import moment from 'moment-timezone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import postService from '../../services/postService';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useCalendarStore } from '../../stores/calendarStore';

interface PostPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post;
  timezone: string;
  onPostUnscheduled: (postId: string) => void;
}

const PostPreviewModal: React.FC<PostPreviewModalProps> = ({ isOpen, onClose, post, timezone, onPostUnscheduled }) => {
  const { currentClient } = useClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const removeCalendarEvent = useCalendarStore((state) => state.removeEvent);

  const unscheduleMutation = useMutation({
    mutationFn: ({ postId, data }: { postId: string; data: UpdatePostData }) =>
        postService.updatePost(postId, data),
    onSuccess: () => {
        toast({
          title: "Post unscheduled successfully!",
        });
        // Invalidate all relevant queries to ensure calendar updates
        queryClient.invalidateQueries({ queryKey: ['scheduledPosts', currentClient?.id] });
        queryClient.invalidateQueries({ queryKey: ['scheduledPostGroups', currentClient?.id] });
        queryClient.invalidateQueries({ queryKey: ['scheduledPostsForQueue'] });
        queryClient.invalidateQueries({ queryKey: ['draftPosts'] });
        queryClient.invalidateQueries({ queryKey: ['postGroups'] });
        removeCalendarEvent(post.id);
        onPostUnscheduled(post.id);
        onClose();
    },
    onError: () => {
        toast({
          title: "Failed to unschedule post.",
          variant: "destructive",
        });
    },
  });

  const handleUnschedule = () => {
      unscheduleMutation.mutate({
          postId: post.id,
          data: {
              status: 'draft',
              scheduled_at: undefined,
          },
      });
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-950 border-gray-800 w-full max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">{post.title}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-gray-400 mb-4">
          Scheduled for: {moment.tz(post.scheduled_at, 'UTC').tz(timezone).format('YYYY-MM-DD HH:mm z')}
        </div>
        {post.media_files && post.media_files.length > 0 && currentClient ? (
          <div className="flex gap-4">
            <div className="w-1/2">
              <AuthenticatedImage 
                key={post.media_files[0]} 
                src={`/api/v1/media/${post.media_files[0]}/thumbnail`} 
                alt="Post media" 
                className="rounded-lg object-cover h-48 w-full border border-gray-800" 
                clientId={currentClient.id}
              />
            </div>
            <div className="w-1/2">
              <p className="mb-4 text-gray-200">{post.content}</p>
            </div>
          </div>
        ) : (
          <p className="mb-4 text-gray-200">{post.content}</p>
        )}
        <DialogFooter>
            <Button
                onClick={handleUnschedule}
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={unscheduleMutation.isPending}
            >
                {unscheduleMutation.isPending ? 'Unscheduling...' : 'Unschedule'}
            </Button>
            <Button onClick={onClose} variant="outline" className="bg-gray-800 border-gray-700 hover:bg-gray-700 text-gray-300">
                Close
            </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostPreviewModal;